import { useAuth } from '@/lib/auth/AuthProvider';
import { Zap, ArrowRight, Shield, FileText, Target } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function LoginPage() {
  const { login, isLoading, isInTeams, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // In Teams, we should auto-authenticate via SSO
  useEffect(() => {
    if (isInTeams && !isAuthenticated && !isLoading) {
      login();
    }
  }, [isInTeams, isAuthenticated, isLoading, login]);

  // If already authenticated (Teams SSO), redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-[#E8E4F0] relative overflow-hidden">
      {/* Decorative shapes - no gradients, just soft solid colors */}
      <div className="absolute top-[-100px] right-[-50px] w-[300px] h-[300px] bg-[#D4C8E8] rounded-full opacity-60" />
      <div className="absolute bottom-[-80px] left-[-60px] w-[250px] h-[250px] bg-[#F5D6C8] rounded-full opacity-50" />

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4">
        {/* Logo and title */}
        <div className="mb-10 text-center animate-fade-in">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary-500 shadow-lg floating">
            <Zap className="h-10 w-10 text-white" />
          </div>
          <h1 className="font-display text-4xl font-bold text-dark-900">
            Impact Agent
          </h1>
          <p className="mt-2 text-base text-dark-500">
            Your AI-powered productivity companion
          </p>
        </div>

        {/* Login card */}
        <div className="w-full max-w-sm animate-slide-up" style={{ animationDelay: '100ms' }}>
          <div className="bg-white rounded-3xl p-8 shadow-xl shadow-dark-900/5">
            <h2 className="text-center text-xl font-bold text-dark-900">
              Welcome back
            </h2>
            <p className="mt-2 text-center text-sm text-dark-500">
              Sign in with your Microsoft 365 account
            </p>

            <button
              onClick={() => login()}
              disabled={isLoading}
              className="btn w-full mt-6 bg-dark-900 text-white font-semibold py-4 rounded-2xl hover:bg-dark-800 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 23 23" fill="currentColor">
                    <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z" />
                  </svg>
                  Sign in with Microsoft
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-dark-200" />
              <span className="text-xs text-dark-400 uppercase tracking-wider">Features</span>
              <div className="flex-1 h-px bg-dark-200" />
            </div>

            {/* Features list - solid color cards like the reference */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#E8E0F5]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-white">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-dark-900">Weekly Summaries</p>
                  <p className="text-xs text-dark-500">AI-generated work reports</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFF0E0]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400 text-dark-900">
                  <Target className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-dark-900">Smart Tracking</p>
                  <p className="text-xs text-dark-500">Automatic keyword detection</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#E0F5F0]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-500 text-white">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-dark-900">Secure & Private</p>
                  <p className="text-xs text-dark-500">Data stays in your OneDrive</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-sm text-dark-500 animate-fade-in" style={{ animationDelay: '400ms' }}>
          Your data is stored securely in your Microsoft 365 tenant
        </p>
      </div>
    </div>
  );
}
